import { useEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  CircularProgress,
  TableCell,
  TableBody,
  Chip,
  Button,
  Box,
  Dialog,
  Stack,
  Alert,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  TablePagination,
  InputAdornment,
  MenuItem,
} from "@mui/material";

import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

// PostgREST caps a single response at 1000 rows (Supabase's db-max-rows), and
// .limit() cannot exceed it. Status/payment/search are filtered client-side, so
// a truncated fetch silently hides rows *before* those filters ever run - which
// is how an approved-unpaid session from March stayed invisible on an admin
// screen whose date range explicitly included March. Page through with .range()
// instead, so "all sessions" really means all of them.
const PAGE_SIZE = 1000;

// Hard backstop so a runaway table can never lock the browser up. If this ever
// trips the user is told, rather than being shown a quietly incomplete list.
const MAX_ROWS = 20000;

export default function Sessions() {
  const { profile } = useAuth();
  // Viewers see every tutor's sessions like an admin, but are read-only: no
  // Review Log (approve/reject/notes). RLS blocks their writes regardless.
  const isAdmin =
    profile?.role === "admin" ||
    profile?.role === "coordinator" ||
    profile?.role === "viewer";
  const canReview = isAdmin && profile?.role !== "viewer";
  const [deleteLoading, setDeleteLoading] = useState(null);

  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [truncated, setTruncated] = useState(false);

  // Sessions Data
  const [sessions, setSessions] = useState([]);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter/Search State
  // Both dates start EMPTY = no bound = every session, all time. The old
  // default (today - 90 days) meant anything older than 90 days was invisible
  // unless someone knew to widen it, which is exactly how stale approved-unpaid
  // sessions went unnoticed. Narrowing is now an explicit choice, not a default.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Review Modal State
  const [selectedSession, setSelectedSession] = useState(null);
  const [adjustment, setAdjustment] = useState({ hours: "", notes: "" });

  // Monotonically increasing id for each fetch. A `type="date"` input fires an
  // event per segment while it's being edited, so changing a date kicks off
  // several overlapping requests - and without this guard whichever response
  // lands LAST wins, not whichever was sent last. That let a stale narrow-range
  // result overwrite a correct wide-range one, leaving the table showing data
  // that didn't match the filters on screen.
  const requestIdRef = useRef(0);

  const fetchSessions = useCallback(async () => {
    if (!profile?.id) return; // Safety check: don't run if profile hasn't loaded yet

    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;

    setLoading(true);
    setFetchError(null);
    setTruncated(false);

    // students/tutors are joined WITHOUT !inner. Both FKs are ON DELETE SET
    // NULL, so an inner join silently drops any log whose student or tutor was
    // deleted - the row still exists and may still be owed money, it just
    // disappears from this page. Left join keeps it visible; the render and the
    // search filter below both tolerate a null name.
    const buildQuery = () => {
      let query = supabase
        .from("lesson_logs")
        .select(
          `
          id,
          date,
          hours,
          rate,
          status,
          admin_notes,
          is_paid,
          student:students(full_name),
          tutor:tutors(profile:profiles(full_name))
        `,
        )
        .order("date", { ascending: false })
        // Tiebreaker: without a unique secondary sort, rows sharing a date can
        // shuffle between .range() pages and get duplicated or skipped.
        .order("id", { ascending: false });

      // Only constrain the range when a bound is actually set. An empty string
      // would be sent as `date=gte.` and rejected by Postgres as an invalid
      // date - which used to surface as a console-only error while the table
      // kept displaying the previous result set.
      if (startDate) query = query.gte("date", startDate);
      if (endDate) query = query.lte("date", endDate);

      // Filter logic based on role
      if (!isAdmin) query = query.eq("tutor_id", profile.id);

      return query;
    };

    try {
      const allRows = [];
      let hitCap = false;

      for (let from = 0; ; from += PAGE_SIZE) {
        const { data, error } = await buildQuery().range(
          from,
          from + PAGE_SIZE - 1,
        );

        if (error) throw error;
        if (!isCurrent()) return; // superseded mid-flight - drop the whole result

        allRows.push(...(data || []));

        if (!data || data.length < PAGE_SIZE) break; // last page
        if (allRows.length >= MAX_ROWS) {
          hitCap = true;
          break;
        }
      }

      if (!isCurrent()) return;
      setSessions(allRows);
      setTruncated(hitCap);
    } catch (error) {
      if (!isCurrent()) return;
      console.error("RLS/Database Error:", error.message);
      // Surface it. Silently keeping the previous rows under the new filter
      // labels is what made this page untrustworthy in the first place.
      setFetchError(error.message);
      setSessions([]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [profile?.id, isAdmin, startDate, endDate]);

  // Initial data fetch
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Any change to the filters can shrink the result set below the current page
  // index, which would render an empty table on a page that still "exists".
  useEffect(() => {
    setPage(0);
  }, [startDate, endDate]);

  // ------------- Filtering Handlers----------------- //

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  //
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0); // Reset to first page when searching
  };

  // ------------- Review Modal Handlers ----------------- //

  const handleOpenReview = (session) => {
    setSelectedSession(session);
    setAdjustment({
      notes: session.admin_notes || "",
    });
  };

  const submitReview = async (status) => {
    const { error } = await supabase
      .from("lesson_logs")
      .update({
        status: status,
        admin_notes: adjustment.notes,
      })
      .eq("id", selectedSession.id);

    if (error) {
      console.error("Review Error:", error.message);
      alert("Could not save this review: " + error.message);
      return;
    }

    setSelectedSession(null);
    fetchSessions();
  };

  // ------------- Delete Handler (Tutor Only) ----------------- //

  const handleDelete = async (session) => {
    if (session.status !== "pending") {
      alert("You can only delete sessions that are still pending.");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to delete this session log? This cannot be undone.",
      )
    ) {
      return;
    }

    setDeleteLoading(session.id);
    try {
      const { error } = await supabase
        .from("lesson_logs")
        .delete()
        .eq("id", session.id);

      if (error) throw error;

      // Optimistic UI update
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
    } catch (error) {
      console.error("Delete Error:", error.message);
      alert("Error deleting session: " + error.message);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPaymentFilter("all");
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  // One-click audit: surfaces every currently approved-but-unpaid session.
  // The date bounds are cleared rather than widened, so this is genuinely all
  // time - and because the fetch now pages past the 1000-row cap, the oldest
  // stranded sessions (the whole point of this button) can't be truncated away
  // before the status/payment filters run.
  const handleFindUnpaidApproved = () => {
    setStatusFilter("approved");
    setPaymentFilter("unpaid");
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  // ------------- Data Processing for Display ----------------- //

  const filteredSessions = sessions.filter((s) => {
    const searchLower = searchQuery.toLowerCase();

    //  Check if log matches the Status filter (if not "all")
    const matchesStatus =
      statusFilter === "all" ? true : s.status === statusFilter;

    //  Check if log matches the Payment filter (if not "all")
    const matchesPayment =
      paymentFilter === "all"
        ? true
        : paymentFilter === "paid"
          ? s.is_paid === true
          : s.is_paid !== true;

    //  Check if log matches the Search Query. Names are nullable now that the
    //  joins are left joins (deleted student/tutor), so default to "".
    const studentName = s.student?.full_name ?? "";
    const tutorName = s.tutor?.profile?.full_name ?? "";
    const matchesSearch = !searchQuery
      ? true
      : isAdmin
        ? studentName.toLowerCase().includes(searchLower) ||
          tutorName.toLowerCase().includes(searchLower)
        : studentName.toLowerCase().includes(searchLower);

    return matchesStatus && matchesPayment && matchesSearch;
  });

  // Calculate number of sessions to display based on pagination
  const paginatedSessions = filteredSessions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const isFiltered =
    Boolean(startDate) ||
    Boolean(endDate) ||
    statusFilter !== "all" ||
    paymentFilter !== "all" ||
    Boolean(searchQuery);

  // --------------------- Render --------------------- //

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={3}>
        <Typography
          variant="h4"
          color="primary.main"
          sx={{ fontWeight: "bold" }}
        >
          {isAdmin ? "All Lesson Sessions" : "My Lesson History"}
        </Typography>
        {!loading && !fetchError && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isFiltered
              ? `Showing ${filteredSessions.length} of ${sessions.length} sessions`
              : `Showing all ${sessions.length} sessions`}
          </Typography>
        )}
      </Box>

      {fetchError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={fetchSessions}>
              Retry
            </Button>
          }
        >
          <Typography variant="body2" fontWeight="bold">
            Could not load sessions.
          </Typography>
          <Typography variant="caption">{fetchError}</Typography>
        </Alert>
      )}

      {truncated && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Showing the first {MAX_ROWS.toLocaleString()} sessions only. Narrow the
          date range to see the rest — this list is incomplete.
        </Alert>
      )}

      {/* FILTER LEVEL*/}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 4, bgcolor: "grey.50", borderRadius: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* LEFT SIDE: Date range + Status/Payment filters + audit shortcut */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              flex: "1 1 auto",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: "500",
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              Date Range:
            </Typography>

            <TextField
              label="From"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              helperText="Leave blank for all time"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: 140,
                bgcolor: "white",
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label="To"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              helperText="Leave blank for all time"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: 140,
                bgcolor: "white",
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              select
              label="Status"
              size="small"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: 140,
                bgcolor: "white",
              }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>

            <TextField
              select
              label="Payment"
              size="small"
              value={paymentFilter}
              onChange={(e) => {
                setPaymentFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: 140,
                bgcolor: "white",
              }}
            >
              <MenuItem value="all">All Payments</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </TextField>

            {isAdmin && (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={handleFindUnpaidApproved}
                sx={{
                  whiteSpace: "nowrap",
                  flex: { xs: "1 1 100%", sm: "0 0 auto" },
                }}
              >
                Find Unpaid Approved (All Time)
              </Button>
            )}

            {isFiltered && (
              <Button
                variant="text"
                size="small"
                onClick={handleResetFilters}
                sx={{ whiteSpace: "nowrap" }}
              >
                Clear Filters
              </Button>
            )}
          </Box>

          {/* RIGHT SIDE: Search Bar */}
          <TextField
            size="small"
            placeholder={
              isAdmin ? "Search tutor or student..." : "Search student..."
            }
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              width: { xs: "100%", md: 300 },
              bgcolor: "white",
              flex: { xs: "1 1 100%", md: "0 0 300px" },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Paper>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          minHeight: 600,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* CONDITIONAL RENDERING: TABLE VS ALERT */}
            {filteredSessions.length > 0 ? (
              <Box sx={{ width: "100%", overflowX: "auto", flexGrow: 1 }}>
                <Table sx={{ tableLayout: "fixed", minWidth: 800 }}>
                  <TableHead sx={{ bgcolor: "grey.100" }}>
                    <TableRow>
                      <TableCell sx={{ width: 110 }}>Session Date</TableCell>
                      {isAdmin && (
                        <TableCell sx={{ width: 160 }}>Tutor</TableCell>
                      )}
                      <TableCell sx={{ width: 160 }}>Student</TableCell>
                      <TableCell sx={{ width: 80 }}>Hours</TableCell>
                      <TableCell sx={{ width: 140 }}>Status</TableCell>
                      <TableCell sx={{ width: 120 }}>Payment</TableCell>
                      <TableCell sx={{ width: 60 }} />
                      <TableCell align="right" sx={{ width: 140 }}>
                        {canReview ? "Review" : "Notes"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedSessions.map((s) => (
                      <TableRow key={s.id} hover sx={{ height: 72 }}>
                        <TableCell>
                          {new Date(s.date).toLocaleDateString()}
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                fontStyle: s.tutor?.profile?.full_name
                                  ? "normal"
                                  : "italic",
                                color: s.tutor?.profile?.full_name
                                  ? "text.primary"
                                  : "error.main",
                              }}
                            >
                              {s.tutor?.profile?.full_name || "Deleted Tutor"}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontStyle: s.student?.full_name
                                ? "normal"
                                : "italic",
                              color: s.student?.full_name
                                ? "text.primary"
                                : "error.main",
                            }}
                          >
                            {s.student?.full_name || "Deleted Student"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <strong>{s.hours}</strong>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(s.status || "pending").toUpperCase()}
                            size="small"
                            color={
                              s.status === "approved"
                                ? "success"
                                : s.status === "rejected"
                                  ? "error"
                                  : "warning"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={s.is_paid ? "PAID" : "UNPAID"}
                            size="small"
                            variant={s.is_paid ? "filled" : "outlined"}
                            color={s.is_paid ? "secondary" : "default"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {!isAdmin && s.status === "pending" && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(s)}
                              disabled={deleteLoading === s.id}
                            >
                              {deleteLoading === s.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {canReview ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenReview(s)}
                            >
                              Review Log
                            </Button>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  s.status === "rejected"
                                    ? "error.main"
                                    : "text.secondary",
                                fontStyle: s.admin_notes ? "normal" : "italic",
                              }}
                            >
                              {s.admin_notes || "No notes"}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              /* 3. ALERT BOX  */
              <Box sx={{ p: 2, flexGrow: 1 }}>
                <Alert
                  severity="info"
                  variant="outlined"
                  action={
                    <Button
                      color="primary"
                      size="small"
                      onClick={handleResetFilters}
                    >
                      Clear Filters
                    </Button>
                  }
                  sx={{ width: "100%" }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {sessions.length === 0
                      ? "No sessions have been logged yet."
                      : "No sessions found matching your criteria."}
                  </Typography>
                  <Typography variant="caption">
                    {sessions.length === 0
                      ? "Once tutors start logging hours they will appear here."
                      : `${sessions.length} sessions exist — try clearing the date range, search query, or the Status/Payment filters.`}
                  </Typography>
                </Alert>
              </Box>
            )}
            {/* PAGINATION */}

            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={filteredSessions.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* ADMIN REVIEW MODAL */}
      <Dialog
        open={Boolean(selectedSession)}
        onClose={() => setSelectedSession(null)}
        fullWidth
        maxWidth="xs"
      >
        {selectedSession ? (
          <>
            <DialogTitle sx={{ fontWeight: "bold", pb: 1 }}>
              Review Lesson Session
            </DialogTitle>

            <DialogContent dividers>
              <Box
                sx={{
                  bgcolor: "grey.50",
                  p: 2,
                  borderRadius: 2,
                  mb: 3,
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      TUTOR
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedSession?.tutor?.profile?.full_name ||
                        "Deleted Tutor"}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      STUDENT
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedSession?.student?.full_name || "Deleted Student"}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      SESSION DATE
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {new Date(selectedSession.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      LOGGED HOURS
                    </Typography>
                    <Chip
                      label={`${selectedSession?.hours} hrs`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      RATE
                    </Typography>
                    <Chip
                      label={`R${selectedSession?.rate}`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                </Stack>
              </Box>

              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                Feedback Note
              </Typography>

              <TextField
                placeholder="e.g., 'Incorrect hours logged.' or 'Great job!'"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={adjustment.notes}
                onChange={(e) =>
                  setAdjustment({ ...adjustment, notes: e.target.value })
                }
                helperText={
                  !adjustment.notes ? "**Required for rejections" : ""
                }
                slotProps={{ formHelperText: { sx: { color: "error.main" } } }}
              />
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
              <Button onClick={() => setSelectedSession(null)} color="inherit">
                Cancel
              </Button>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  onClick={() => submitReview("rejected")}
                  variant="outlined"
                  color="error"
                  disabled={!adjustment.notes} // Prevent rejecting without a reason
                >
                  Reject
                </Button>
                <Button
                  onClick={() => submitReview("approved")}
                  variant="contained"
                  color="success"
                  disableElevation
                >
                  Approve
                </Button>
              </Box>
            </DialogActions>
          </>
        ) : (
          <Box sx={{ height: 400 }} />
        )}
      </Dialog>
    </Container>
  );
}
